import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LangProvider } from "@/context/LangContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { Toaster } from "sonner";
import { lazy, Suspense } from "react";
import LoginPage from "@/pages/LoginPage";
import ProtectedLayout from "@/components/Layout";
import { Loader2 } from "lucide-react";

// Lazy-loaded pages for code splitting
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const AcceptInvitePage = lazy(() => import("@/pages/AcceptInvitePage"));
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const QuotationFormPage = lazy(() => import("@/pages/QuotationFormPage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const ProfilePage = lazy(() => import("@/pages/ProfilePage"));
const TrashPage = lazy(() => import("@/pages/TrashPage"));
const ClientsPage = lazy(() => import("@/pages/ClientsPage"));
const AnalyticsPage = lazy(() => import("@/pages/AnalyticsPage"));

const PageLoader = () => (
  <div className="flex items-center justify-center h-64">
    <Loader2 className="w-5 h-5 animate-spin text-[#262626]/20 dark:text-[#E5E7EB]/20" />
  </div>
);

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F7F7]">
        <Loader2 className="w-6 h-6 animate-spin text-[#262626]/30" />
      </div>
    );
  }

  return (
    <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route
        path="/login"
        element={user ? <Navigate to="/dashboard" replace /> : <LoginPage />}
      />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />

      {/* All protected routes share ProtectedLayout — sidebar stays mounted */}
      <Route element={<ProtectedLayout />}>
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/quotations/new" element={<QuotationFormPage />} />
        <Route path="/quotations/:id" element={<QuotationFormPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/analytics" element={<AnalyticsPage />} />
        <Route path="/clients" element={<ClientsPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/trash" element={<TrashPage />} />
      </Route>

      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
      <LangProvider>
        <AuthProvider>
          {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
          <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
            <AppRoutes />
            <Toaster
              position="top-right"
              toastOptions={{
                style: {
                  fontFamily: "'IBM Plex Sans Arabic', 'IBM Plex Sans', sans-serif",
                  borderRadius: "10px",
                  border: "1px solid #E5E5E5",
                },
              }}
            />
          </GoogleOAuthProvider>
        </AuthProvider>
      </LangProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
